export function parseJsonPayload(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (
    !(
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    )
  ) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}

export type FlatLine = { depth: number; text: string };

export function flattenValue(value: unknown, depth = 0, key?: string): FlatLine[] {
  const lines: FlatLine[] = [];
  const prefix = key != null ? `${key}: ` : '';

  if (value === null || value === undefined) {
    lines.push({ depth, text: `${prefix}—` });
    return lines;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    lines.push({ depth, text: `${prefix}${String(value)}` });
    return lines;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      lines.push({ depth, text: `${prefix}(empty)` });
      return lines;
    }
    if (key != null) {
      lines.push({ depth, text: key });
    }
    value.forEach((item, i) => {
      if (item !== null && typeof item === 'object') {
        lines.push({ depth: depth + (key != null ? 1 : 0), text: `${i + 1}.` });
        lines.push(...flattenValue(item, depth + (key != null ? 2 : 1)));
      } else {
        lines.push({
          depth: depth + (key != null ? 1 : 0),
          text: `${i + 1}. ${String(item)}`,
        });
      }
    });
    return lines;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      lines.push({ depth, text: `${prefix}(empty)` });
      return lines;
    }
    if (key != null) {
      lines.push({ depth, text: key });
    }
    const childDepth = depth + (key != null ? 1 : 0);
    for (const [k, v] of entries) {
      lines.push(...flattenValue(v, childDepth, k));
    }
    return lines;
  }

  lines.push({ depth, text: `${prefix}${String(value)}` });
  return lines;
}

export function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

export function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' ? v : undefined;
}

export function asArray(v: unknown): unknown[] | undefined {
  return Array.isArray(v) ? v : undefined;
}
